import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import LeadCard from "@/components/feature/LeadCard";
import { useSeo } from "@/hooks/useSeo";
import { leadCategories, leads } from "@/mocks/leads";

const PAGE_SIZE = 6;

export default function LeadsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") ?? "all";
  const keyword = searchParams.get("keyword") ?? "";
  const [draftKeyword, setDraftKeyword] = useState(keyword);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useSeo({
    title: "爆料线索库 | 已核实线索与内幕整理 - 直曝",
    description:
      "直曝爆料线索库汇总经编辑初步核实的公开线索，涵盖消费维权、职场内幕、行业观察、民生身边事、网络乱象与公益求助。支持按类别筛选与关键词检索，看看别人是如何通过网络爆料投稿的。",
    keywords: "网络爆料,网络爆料线索,爆料线索库,消费维权,职场内幕,行业观察",
    path: "/leads",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "直曝爆料线索库",
        url: "https://ygtabln.cn/leads",
        inLanguage: "zh-CN",
        description:
          "经编辑初步核实的公开线索合集，涵盖消费维权、职场内幕、行业观察、民生身边事、网络乱象与公益求助。",
        mainEntity: {
          "@type": "ItemList",
          numberOfItems: leads.length,
          itemListElement: leads.map((lead, index) => ({
            "@type": "ListItem",
            position: index + 1,
            url: `https://ygtabln.cn/leads/${lead.id}`,
            name: lead.title,
          })),
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://ygtabln.cn/" },
          { "@type": "ListItem", position: 2, name: "爆料线索库", item: "https://ygtabln.cn/leads" },
        ],
      },
    ],
  });

  const filtered = useMemo(() => {
    const term = keyword.trim().toLowerCase();
    return leads.filter((lead) => {
      const matchCategory = activeCategory === "all" || lead.categorySlug === activeCategory;
      if (!matchCategory) return false;
      if (!term) return true;
      const haystack = `${lead.title}${lead.summary}${lead.tags.join("")}${lead.category}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [activeCategory, keyword]);

  const visibleLeads = filtered.slice(0, visibleCount);

  const handleCategoryChange = (slug: string) => {
    const next = new URLSearchParams(searchParams);
    if (slug === "all") {
      next.delete("category");
    } else {
      next.set("category", slug);
    }
    setSearchParams(next);
    setVisibleCount(PAGE_SIZE);
  };

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    const value = draftKeyword.trim();
    if (value) {
      next.set("keyword", value);
    } else {
      next.delete("keyword");
    }
    setSearchParams(next);
    setVisibleCount(PAGE_SIZE);
  };

  const activeCategoryName =
    leadCategories.find((category) => category.slug === activeCategory)?.name ?? "全部线索";

  return (
    <>
      <div className="bg-background-100 border-b border-background-200">
        <div className="px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-6xl mx-auto">
            <Breadcrumbs items={[{ label: "爆料线索库" }]} />
            <h1 className="mt-5 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
              爆料线索库
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
              这里收录已经完成初步核实、并经匿名化处理的公开线索。它们大多来自一条普普通通的投稿消息。
            </p>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
            <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="线索类别筛选">
              <button
                type="button"
                role="tab"
                aria-selected={activeCategory === "all"}
                onClick={() => handleCategoryChange("all")}
                className={`px-4 py-2 rounded-full text-sm font-label whitespace-nowrap transition-colors cursor-pointer ${
                  activeCategory === "all"
                    ? "bg-foreground-950 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                全部
              </button>
              {leadCategories.map((category) => (
                <button
                  key={category.slug}
                  type="button"
                  role="tab"
                  aria-selected={activeCategory === category.slug}
                  onClick={() => handleCategoryChange(category.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-label whitespace-nowrap transition-colors cursor-pointer ${
                    activeCategory === category.slug
                      ? "bg-foreground-950 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>

            <form onSubmit={handleSearch} className="flex items-center gap-2 w-full lg:w-80" role="search">
              <div className="relative flex-1">
                <i
                  className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-base pointer-events-none"
                  aria-hidden="true"
                ></i>
                <input
                  type="search"
                  name="keyword"
                  value={draftKeyword}
                  onChange={(e) => setDraftKeyword(e.target.value)}
                  placeholder="搜索线索关键词"
                  aria-label="搜索线索关键词"
                  className="w-full pl-10 pr-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                />
              </div>
              <button
                type="submit"
                className="px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                搜索
              </button>
            </form>
          </div>

          <p className="mt-6 text-sm text-foreground-500 font-label">
            当前：<span className="text-foreground-800">{activeCategoryName}</span>
            {keyword && (
              <>
                {" · "}关键词：<span className="text-foreground-800">{keyword}</span>
              </>
            )}
            {" · "}共 <span className="text-foreground-800">{filtered.length}</span> 条
          </p>

          {visibleLeads.length > 0 ? (
            <>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {visibleLeads.map((lead) => (
                  <LeadCard key={lead.id} lead={lead} />
                ))}
              </div>

              {visibleCount < filtered.length && (
                <div className="mt-10 text-center">
                  <button
                    type="button"
                    onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-foreground-300 text-foreground-900 text-sm font-label font-medium whitespace-nowrap hover:bg-foreground-950 hover:text-background-50 transition-colors cursor-pointer"
                  >
                    加载更多线索
                    <i className="ri-arrow-down-line w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
              <span className="mx-auto w-12 h-12 rounded-full bg-background-200 text-foreground-500 flex items-center justify-center">
                <i className="ri-search-eye-line text-2xl" aria-hidden="true"></i>
              </span>
              <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">没有找到匹配的线索</h2>
              <p className="mt-2 text-sm text-foreground-600">
                换个关键词试试，或者直接把你手上的材料投给我们。
              </p>
              <Link
                to="/submit"
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-send-plane-fill w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
                我要投稿
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
}